export type EasingFn = (t: number) => number;

export const Easing = {
  linear: (t: number) => t,
  easeInQuad: (t: number) => t * t,
  easeOutQuad: (t: number) => t * (2 - t),
  easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeOutCubic: (t: number) => --t * t * t + 1,
  easeInOutCubic: (t: number) =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
  easeOutBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
  easeOutElastic: (t: number) => {
    if (t === 0 || t === 1) return t;
    return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1;
  },
};

interface TweenOptions {
  from: number;
  to: number;
  duration: number;
  easing?: EasingFn;
  onUpdate: (value: number) => void;
  onComplete?: () => void;
}

interface ActiveTween {
  startTime: number;
  options: TweenOptions;
}

const activeTweens: ActiveTween[] = [];

export function tween(options: TweenOptions): { cancel: () => void } {
  const entry: ActiveTween = {
    startTime: performance.now(),
    options: { easing: Easing.easeOutCubic, ...options },
  };
  activeTweens.push(entry);
  return {
    cancel: () => {
      const idx = activeTweens.indexOf(entry);
      if (idx !== -1) activeTweens.splice(idx, 1);
    },
  };
}

export function updateTweens(): void {
  const now = performance.now();
  for (let i = activeTweens.length - 1; i >= 0; i--) {
    const tw = activeTweens[i];
    const elapsed = now - tw.startTime;
    const progress = Math.min(elapsed / tw.options.duration, 1);
    const easedProgress = tw.options.easing!(progress);
    const value = tw.options.from + (tw.options.to - tw.options.from) * easedProgress;
    tw.options.onUpdate(value);
    if (progress >= 1) {
      activeTweens.splice(i, 1);
      tw.options.onComplete?.();
    }
  }
}

export function pulsate(time: number, speed: number = 2, min: number = 0.6, max: number = 1.0): number {
  const t = (Math.sin(time * speed) + 1) / 2;
  return min + (max - min) * t;
}
